import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabase';
import MoneyInput from '../components/MoneyInput';
import VinculacionModal from '../components/VinculacionModal';

const formatMoney = (n) => '$' + (n || 0).toLocaleString('es-AR');
const hoyStr = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; };

const emptyForm = () => ({ monto: '', fecha: hoyStr(), concepto: '', cliente_nombre: '', cliente_id: '', medio_cobro: 'Efectivo', expediente_id: '', tramite_id: '', deuda_id: '' });

export default function IngresoModal({ open, onClose, editing = null, onSaved }) {
  const [clientes, setClientes] = useState([]);
  const [expedientes, setExpedientes] = useState([]);
  const [tramites, setTramites] = useState([]);
  const [deudas, setDeudas] = useState([]);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const [form, setForm] = useState(emptyForm());
  const [busquedaCliente, setBusquedaCliente] = useState('');
  const [dropdownClienteOpen, setDropdownClienteOpen] = useState(false);
  const [modalOtrasDeudas, setModalOtrasDeudas] = useState(false);
  const [busquedaDeuda, setBusquedaDeuda] = useState('');
  const [modalCrearDeuda, setModalCrearDeuda] = useState(false);
  const [formDeuda, setFormDeuda] = useState({ concepto: '', monto_total: '', fecha_vencimiento: '', expediente_id: '', tramite_id: '', cliente_id: '' });
  const [busquedaClienteDeuda, setBusquedaClienteDeuda] = useState('');
  const [dropdownClienteDeudaOpen, setDropdownClienteDeudaOpen] = useState(false);
  const [vincularOpen, setVincularOpen] = useState(false);
  const [vincularDeudaOpen, setVincularDeudaOpen] = useState(false);

  const editingId = editing?.id || null;

  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };

  const fetchDeudas = useCallback(async () => {
    const { data } = await supabase.from('deudas')
      .select('id, concepto, deudor_nombre, monto_total, monto_cobrado, cliente_id, estado, clientes(nombre, apellido)')
      .order('created_at', { ascending: false });
    setDeudas(data || []);
  }, []);

  useEffect(() => {
    if (!open) return;
    (async () => {
      const [cli, exp, tra] = await Promise.all([
        supabase.from('clientes').select('id, nombre, apellido').eq('eliminado', false).order('nombre'),
        supabase.from('expedientes').select('id, numero, caratula').eq('eliminado', false).order('caratula'),
        supabase.from('tramites').select('id, nombre').eq('eliminado', false).order('nombre'),
      ]);
      const cliData = cli.data || [];
      setClientes(cliData);
      setExpedientes(exp.data || []);
      setTramites(tra.data || []);
      fetchDeudas();
      if (editing) {
        setForm({
          monto: editing.monto || '', fecha: editing.fecha || '', concepto: editing.concepto || '',
          cliente_nombre: editing.cliente_nombre || '', cliente_id: editing.cliente_id || '',
          medio_cobro: editing.medio_cobro || 'Efectivo', expediente_id: editing.expediente_id || '',
          tramite_id: editing.tramite_id || '', deuda_id: editing.deuda_id || '',
        });
        const cs = cliData.find(c => c.id === (editing.cliente_id || ''));
        setBusquedaCliente(cs ? `${cs.nombre} ${cs.apellido}` : '');
      } else {
        setForm(emptyForm());
        setBusquedaCliente('');
      }
    })();
  }, [open, editing, fetchDeudas]);

  // Etiqueta del expediente/trámite vinculado al ingreso
  let vinculo = null;
  if (form.expediente_id) {
    const e = expedientes.find(x => x.id === form.expediente_id);
    vinculo = { icon: '📁', tipo: 'Expediente', label: e ? `${e.numero ? e.numero + ' — ' : ''}${e.caratula || ''}` : 'Expediente' };
  } else if (form.tramite_id) {
    const t = tramites.find(x => x.id === form.tramite_id);
    vinculo = { icon: '📋', tipo: 'Trámite', label: t ? t.nombre : 'Trámite' };
  }

  // Etiqueta del expediente/trámite vinculado a la deuda rápida
  let vinculoDeuda = null;
  if (formDeuda.expediente_id) {
    const e = expedientes.find(x => x.id === formDeuda.expediente_id);
    vinculoDeuda = { icon: '📁', tipo: 'Expediente', label: e ? `${e.numero ? e.numero + ' — ' : ''}${e.caratula || ''}` : 'Expediente' };
  } else if (formDeuda.tramite_id) {
    const t = tramites.find(x => x.id === formDeuda.tramite_id);
    vinculoDeuda = { icon: '📋', tipo: 'Trámite', label: t ? t.nombre : 'Trámite' };
  }

  async function saveDeudaRapida() {
    if (!formDeuda.concepto.trim()) { showToast('Ingresá un concepto', 'error'); return; }
    if (!formDeuda.monto_total || parseFloat(formDeuda.monto_total) <= 0) { showToast('Ingresá un monto válido', 'error'); return; }
    const clienteId = formDeuda.cliente_id || form.cliente_id || null;
    const payload = {
      concepto: formDeuda.concepto,
      monto_total: parseFloat(formDeuda.monto_total),
      monto_cobrado: 0,
      estado: 'vigente',
      cliente_id: clienteId,
      expediente_id: formDeuda.expediente_id || null,
      tramite_id: formDeuda.tramite_id || null,
      fecha_vencimiento: formDeuda.fecha_vencimiento || null,
      deudor_nombre: clienteId ? null : (form.cliente_nombre || null),
    };
    const { data, error } = await supabase.from('deudas').insert(payload).select('id').single();
    if (error) { showToast('Error al crear la deuda', 'error'); return; }
    await fetchDeudas();
    setForm(f => ({ ...f, deuda_id: data.id }));
    setModalCrearDeuda(false);
    setFormDeuda({ concepto: '', monto_total: '', fecha_vencimiento: '', expediente_id: '', tramite_id: '', cliente_id: '' });
    showToast('Deuda creada y vinculada');
  }

  async function saveIngreso() {
    if (!form.monto || parseFloat(form.monto) <= 0) { showToast('Ingresá un monto válido', 'error'); return; }
    setSaving(true);
    const montoNuevo = parseFloat(form.monto);
    const ingresoData = {
      monto: montoNuevo,
      fecha: form.fecha || null,
      concepto: form.concepto,
      cliente_nombre: form.cliente_id ? null : form.cliente_nombre,
      cliente_id: form.cliente_id || null,
      medio_cobro: form.medio_cobro,
      expediente_id: form.expediente_id || null,
      tramite_id: form.tramite_id || null,
      deuda_id: form.deuda_id || null,
    };

    let error;
    if (editingId) {
      ({ error } = await supabase.from('ingresos').update(ingresoData).eq('id', editingId));
    } else {
      const result = await supabase.from('ingresos').insert(ingresoData).select('id').single();
      error = result.error;
    }
    if (error) { setSaving(false); showToast('Error al guardar ingreso', 'error'); console.error(error); return; }

    // Sincronizar el saldo de la deuda afectada: tanto al crear como al editar
    // (vincular una deuda a un ingreso ya existente, cambiar de deuda, desvincular
    // o modificar el monto). Antes solo se actualizaba al crear (!editingId).
    const deudaAnteriorId = editingId ? (editing?.deuda_id || null) : null;
    const montoAnterior = editingId ? (editing?.monto || 0) : 0;
    const deudaNuevaId = form.deuda_id || null;

    if (deudaAnteriorId && deudaAnteriorId !== deudaNuevaId) {
      const { data: d } = await supabase.from('deudas').select('monto_total, monto_cobrado, estado').eq('id', deudaAnteriorId).single();
      if (d) {
        const cobrado = Math.max(0, (d.monto_cobrado || 0) - montoAnterior);
        const estado = cobrado < (d.monto_total || 0) ? (d.estado === 'cobrada' ? 'vigente' : d.estado) : d.estado;
        await supabase.from('deudas').update({ monto_cobrado: cobrado, estado }).eq('id', deudaAnteriorId);
      }
    }

    if (deudaNuevaId) {
      const { data: d } = await supabase.from('deudas').select('monto_total, monto_cobrado, estado').eq('id', deudaNuevaId).single();
      if (d) {
        const base = deudaAnteriorId === deudaNuevaId ? Math.max(0, (d.monto_cobrado || 0) - montoAnterior) : (d.monto_cobrado || 0);
        const cobrado = base + montoNuevo;
        await supabase.from('deudas').update({
          monto_cobrado: cobrado,
          estado: cobrado >= (d.monto_total || 0) ? 'cobrada' : d.estado,
        }).eq('id', deudaNuevaId);
      }
    }

    setSaving(false);
    showToast(editingId ? 'Ingreso actualizado ✓' : 'Ingreso registrado ✓');
    onSaved?.();
    onClose();
  }

  async function eliminarIngreso() {
    if (!window.confirm('¿Eliminar este ingreso?')) return;
    const { error } = await supabase.rpc('eliminar_ingreso', { p_ingreso_id: editingId });
    if (error) { showToast('Error al eliminar', 'error'); return; }
    onSaved?.();
    onClose();
  }

  if (!open) return null;

  return (
    <>
      <div className="modal-overlay" onMouseDown={ev => ev.target === ev.currentTarget && onClose()}>
        <div className="modal" style={{ width: 600 }}>
          <div className="modal-header">
            <div className="modal-title">{editingId ? 'Editar Ingreso' : 'Registrar Ingreso'}</div>
            <button className="modal-close" onClick={onClose}>✕</button>
          </div>
          <div className="modal-body" style={{ padding: '16px 20px' }}>
            <div className="form-grid" style={{ gap: 10 }}>

              <div className="form-group">
                <label className="form-label">Monto ($) *</label>
                <MoneyInput value={form.monto} onChange={v => setForm(f => ({ ...f, monto: v }))} autoFocus />
              </div>
              <div className="form-group">
                <label className="form-label">Fecha</label>
                <input className="form-input" type="date" value={form.fecha} onChange={e => setForm(f => ({ ...f, fecha: e.target.value }))} />
              </div>

              <div className="form-group form-full">
                <label className="form-label">Concepto</label>
                <input className="form-input" value={form.concepto} onChange={e => setForm(f => ({ ...f, concepto: e.target.value }))} placeholder="Ej: Anticipo de honorarios, Consulta" />
              </div>

              <div className="form-group">
                <label className="form-label">Medio de cobro</label>
                <select className="form-select" value={form.medio_cobro} onChange={e => setForm(f => ({ ...f, medio_cobro: e.target.value }))}>
                  <option>Efectivo</option>
                  <option>Transferencia</option>
                </select>
              </div>

              <div className="form-group form-full">
                <label className="form-label">Expediente / Trámite vinculado</label>
                {vinculo ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', background: 'var(--surface2)', border: '0.5px solid var(--border)', borderRadius: 8 }}>
                    <span style={{ fontSize: 16, flexShrink: 0 }}>{vinculo.icon}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 10, color: 'var(--text4)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>{vinculo.tipo}</div>
                      <div style={{ fontSize: 13, color: 'var(--text)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{vinculo.label}</div>
                    </div>
                    <button className="btn btn-ghost btn-sm" onClick={() => setVincularOpen(true)} style={{ flexShrink: 0 }}>Cambiar</button>
                  </div>
                ) : (
                  <button className="btn btn-ghost" onClick={() => setVincularOpen(true)} style={{ width: '100%', justifyContent: 'center' }}>
                    + Vincular expediente o trámite
                  </button>
                )}
              </div>

              <div className="form-group form-full">
                <label className="form-label">Cliente registrado</label>
                <div style={{ position: 'relative', width: '100%' }}>
                  <input
                    className="form-input"
                    style={{ width: '100%', boxSizing: 'border-box' }}
                    placeholder="Seleccionar cliente registrado"
                    value={busquedaCliente}
                    onChange={e => {
                      setBusquedaCliente(e.target.value);
                      if (!e.target.value) setForm(f => ({ ...f, cliente_id: '', deuda_id: '' }));
                    }}
                    onFocus={() => setDropdownClienteOpen(true)}
                    onBlur={() => setTimeout(() => setDropdownClienteOpen(false), 150)}
                  />
                  {dropdownClienteOpen && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, zIndex: 200, maxHeight: 200, overflowY: 'auto', marginTop: 4, boxShadow: '0 4px 16px rgba(0,0,0,0.3)' }}>
                      <div
                        onMouseDown={() => { setForm(f => ({ ...f, cliente_id: '', cliente_nombre: '', deuda_id: '' })); setBusquedaCliente(''); }}
                        style={{ padding: '8px 14px', cursor: 'pointer', fontSize: 13, color: 'var(--text4)', borderBottom: '0.5px solid var(--border)' }}
                      >
                        Sin vincular / Manual
                      </div>
                      {clientes.filter(c => {
                        const t = busquedaCliente.toLowerCase();
                        return !t || `${c.nombre} ${c.apellido}`.toLowerCase().includes(t) || (c.dni || '').includes(t);
                      }).map(c => (
                        <div key={c.id}
                          onMouseDown={() => {
                            const deudaActual = deudas.find(d => d.id === form.deuda_id);
                            const deudaPertenece = deudaActual?.cliente_id === c.id;
                            setForm(f => ({ ...f, cliente_id: c.id, cliente_nombre: '', deuda_id: deudaPertenece ? f.deuda_id : '' }));
                            setBusquedaCliente(`${c.nombre} ${c.apellido}`);
                          }}
                          style={{ padding: '8px 14px', cursor: 'pointer', fontSize: 13, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: form.cliente_id === c.id ? 'var(--blue-light)' : 'transparent', color: form.cliente_id === c.id ? 'var(--blue)' : 'var(--text2)' }}
                        >
                          <span>{c.nombre} {c.apellido}</span>
                          {form.cliente_id === c.id && <span style={{ fontSize: 12 }}>✓</span>}
                        </div>
                      ))}
                      {clientes.filter(c => { const t = busquedaCliente.toLowerCase(); return !t || `${c.nombre} ${c.apellido}`.toLowerCase().includes(t) || (c.dni || '').includes(t); }).length === 0 && (
                        <div style={{ padding: '10px 14px', color: 'var(--text4)', fontSize: 13 }}>Sin resultados</div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {!form.cliente_id && (
                <div className="form-group form-full">
                  <label className="form-label">Nombre del cliente (manual)</label>
                  <input className="form-input" value={form.cliente_nombre} onChange={e => setForm(f => ({ ...f, cliente_nombre: e.target.value }))} placeholder="Ingresá el nombre sin crear ficha" />
                </div>
              )}

              <div className="form-group form-full">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                  <label className="form-label" style={{ margin: 0 }}>Deuda vinculada</label>
                  <button type="button" className="btn btn-ghost btn-xs" onClick={() => { setFormDeuda({ concepto: '', monto_total: '', fecha_vencimiento: '', expediente_id: '', tramite_id: '', cliente_id: form.cliente_id || '' }); setBusquedaClienteDeuda(busquedaCliente); setModalCrearDeuda(true); }}>+ Crear deuda</button>
                </div>
                {form.deuda_id ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: 'var(--blue-light)', border: '1px solid var(--blue)', borderRadius: 8, fontSize: 13 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ color: 'var(--blue)', fontWeight: 500 }}>
                        {deudas.find(d => d.id === form.deuda_id)?.concepto}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text4)', marginTop: 2 }}>
                        Pendiente: {formatMoney((deudas.find(d => d.id === form.deuda_id)?.monto_total || 0) - (deudas.find(d => d.id === form.deuda_id)?.monto_cobrado || 0))}
                      </div>
                    </div>
                    <span onClick={() => setForm(f => ({ ...f, deuda_id: '' }))} style={{ cursor: 'pointer', color: 'var(--blue)', fontWeight: 700, fontSize: 16 }}>×</span>
                  </div>
                ) : form.cliente_id ? (
                  <select className="form-select" value="" onChange={e => {
                    if (e.target.value === '__otras__') { setModalOtrasDeudas(true); }
                    else if (e.target.value) { setForm(f => ({ ...f, deuda_id: e.target.value })); }
                  }}>
                    <option value="">Sin vincular</option>
                    {deudas.filter(d => d.cliente_id === form.cliente_id).map(d => (
                      <option key={d.id} value={d.id}>
                        {d.concepto} — pendiente {formatMoney((d.monto_total || 0) - (d.monto_cobrado || 0))}
                      </option>
                    ))}
                    {deudas.filter(d => d.cliente_id === form.cliente_id).length === 0 && (
                      <option disabled>— Este cliente no tiene deudas —</option>
                    )}
                    <option value="__otras__">🔍 Buscar en todas las deudas...</option>
                  </select>
                ) : (
                  <button type="button" className="btn btn-ghost" style={{ fontSize: 13, justifyContent: 'flex-start' }} onClick={() => setModalOtrasDeudas(true)}>
                    🔍 Buscar deuda...
                  </button>
                )}
              </div>

            </div>
          </div>
          <div className="modal-footer">
            {editingId && (
              <button className="btn btn-danger btn-sm" onClick={eliminarIngreso} style={{ marginRight: 'auto' }}>Eliminar</button>
            )}
            <button className="btn btn-ghost" onClick={onClose}>Cancelar</button>
            <button className="btn btn-primary" onClick={saveIngreso} disabled={saving}>{saving ? 'Guardando…' : 'Guardar Ingreso'}</button>
          </div>
        </div>
      </div>

      {modalOtrasDeudas && (
        <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && setModalOtrasDeudas(false)} style={{ zIndex: 1100 }}>
          <div className="modal" style={{ width: 560 }}>
            <div className="modal-header">
              <div className="modal-title">Buscar deuda</div>
              <button className="modal-close" onClick={() => setModalOtrasDeudas(false)}>✕</button>
            </div>
            <div className="modal-body" style={{ padding: '14px 22px' }}>
              <input
                className="form-input"
                placeholder="Buscar por concepto o cliente..."
                value={busquedaDeuda}
                onChange={e => setBusquedaDeuda(e.target.value)}
                autoFocus
                style={{ marginBottom: 12 }}
              />
              <div style={{ maxHeight: 340, overflowY: 'auto' }}>
                {deudas.filter(d => {
                  const t = busquedaDeuda.toLowerCase();
                  const clienteNombre = d.clientes ? `${d.clientes.nombre} ${d.clientes.apellido}` : d.deudor_nombre || '';
                  return !t || d.concepto?.toLowerCase().includes(t) || clienteNombre.toLowerCase().includes(t);
                }).map(d => {
                  const clienteNombre = d.clientes ? `${d.clientes.nombre} ${d.clientes.apellido}` : d.deudor_nombre || '—';
                  const pendiente = (d.monto_total || 0) - (d.monto_cobrado || 0);
                  return (
                    <div key={d.id} onClick={() => { setForm(f => ({ ...f, deuda_id: d.id })); setModalOtrasDeudas(false); setBusquedaDeuda(''); }}
                      style={{ padding: '10px 14px', borderRadius: 8, marginBottom: 6, border: '0.5px solid var(--border)', background: 'var(--surface2)', cursor: 'pointer', transition: 'border-color 0.15s' }}
                      onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--blue)'}
                      onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{d.concepto}</div>
                          <div style={{ fontSize: 11, color: 'var(--text4)', marginTop: 2 }}>{clienteNombre}</div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: pendiente > 0 ? 'var(--amber)' : 'var(--green)', fontFamily: 'DM Mono' }}>{formatMoney(pendiente)}</div>
                          <div style={{ fontSize: 10, color: 'var(--text4)' }}>pendiente</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
                {deudas.filter(d => {
                  const t = busquedaDeuda.toLowerCase();
                  const cn = d.clientes ? `${d.clientes.nombre} ${d.clientes.apellido}` : d.deudor_nombre || '';
                  return !t || d.concepto?.toLowerCase().includes(t) || cn.toLowerCase().includes(t);
                }).length === 0 && (
                  <div style={{ textAlign: 'center', color: 'var(--text4)', padding: 32, fontSize: 13 }}>Sin resultados</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {modalCrearDeuda && (
        <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && setModalCrearDeuda(false)} style={{ zIndex: 1100 }}>
          <div className="modal" style={{ width: 480 }}>
            <div className="modal-header">
              <div className="modal-title">Crear nueva deuda</div>
              <button className="modal-close" onClick={() => setModalCrearDeuda(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group form-full">
                  <label className="form-label">Concepto *</label>
                  <input className="form-input" value={formDeuda.concepto} onChange={e => setFormDeuda(f => ({ ...f, concepto: e.target.value }))} placeholder="Ej: Honorarios juicio laboral" autoFocus />
                </div>
                <div className="form-group">
                  <label className="form-label">Monto total ($) *</label>
                  <MoneyInput value={formDeuda.monto_total} onChange={v => setFormDeuda(f => ({ ...f, monto_total: v }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Fecha de vencimiento</label>
                  <input className="form-input" type="date" value={formDeuda.fecha_vencimiento} onChange={e => setFormDeuda(f => ({ ...f, fecha_vencimiento: e.target.value }))} />
                </div>
                <div className="form-group form-full">
                  <label className="form-label">Cliente vinculado</label>
                  <div style={{ position: 'relative', width: '100%' }}>
                    <input
                      className="form-input"
                      style={{ width: '100%', boxSizing: 'border-box' }}
                      placeholder="Seleccionar cliente registrado"
                      value={busquedaClienteDeuda}
                      onChange={e => { setBusquedaClienteDeuda(e.target.value); if (!e.target.value) setFormDeuda(f => ({ ...f, cliente_id: '' })); }}
                      onFocus={() => setDropdownClienteDeudaOpen(true)}
                      onBlur={() => setTimeout(() => setDropdownClienteDeudaOpen(false), 150)}
                    />
                    {dropdownClienteDeudaOpen && (
                      <div style={{ position: 'absolute', bottom: '100%', left: 0, right: 0, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, zIndex: 200, maxHeight: 200, overflowY: 'auto', marginBottom: 4, boxShadow: '0 -4px 16px rgba(0,0,0,0.3)' }}>
                        <div onMouseDown={() => { setFormDeuda(f => ({ ...f, cliente_id: '' })); setBusquedaClienteDeuda(''); }}
                          style={{ padding: '8px 14px', cursor: 'pointer', fontSize: 13, color: 'var(--text4)', borderBottom: '0.5px solid var(--border)' }}>
                          Sin vincular
                        </div>
                        {clientes.filter(c => { const t = busquedaClienteDeuda.toLowerCase(); return !t || `${c.nombre} ${c.apellido}`.toLowerCase().includes(t) || (c.dni || '').includes(t); }).map(c => (
                          <div key={c.id} onMouseDown={() => { setFormDeuda(f => ({ ...f, cliente_id: c.id })); setBusquedaClienteDeuda(`${c.nombre} ${c.apellido}`); }}
                            style={{ padding: '8px 14px', cursor: 'pointer', fontSize: 13, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: formDeuda.cliente_id === c.id ? 'var(--blue-light)' : 'transparent', color: formDeuda.cliente_id === c.id ? 'var(--blue)' : 'var(--text2)' }}>
                            <span>{c.nombre} {c.apellido}</span>
                            {formDeuda.cliente_id === c.id && <span style={{ fontSize: 12 }}>✓</span>}
                          </div>
                        ))}
                        {clientes.filter(c => { const t = busquedaClienteDeuda.toLowerCase(); return !t || `${c.nombre} ${c.apellido}`.toLowerCase().includes(t); }).length === 0 && (
                          <div style={{ padding: '10px 14px', color: 'var(--text4)', fontSize: 13 }}>Sin resultados</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="form-group form-full">
                  <label className="form-label">Expediente / Trámite vinculado</label>
                  {vinculoDeuda ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', background: 'var(--surface2)', border: '0.5px solid var(--border)', borderRadius: 8 }}>
                      <span style={{ fontSize: 16, flexShrink: 0 }}>{vinculoDeuda.icon}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 10, color: 'var(--text4)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>{vinculoDeuda.tipo}</div>
                        <div style={{ fontSize: 13, color: 'var(--text)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{vinculoDeuda.label}</div>
                      </div>
                      <button className="btn btn-ghost btn-sm" onClick={() => setVincularDeudaOpen(true)} style={{ flexShrink: 0 }}>Cambiar</button>
                    </div>
                  ) : (
                    <button className="btn btn-ghost" onClick={() => setVincularDeudaOpen(true)} style={{ width: '100%', justifyContent: 'center' }}>
                      + Vincular expediente o trámite
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setModalCrearDeuda(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={saveDeudaRapida}>Crear y vincular</button>
            </div>
          </div>
        </div>
      )}

      <VinculacionModal
        open={vincularOpen}
        onClose={() => setVincularOpen(false)}
        tabs={['expedientes', 'tramites']}
        value={{ expediente_id: form.expediente_id, tramite_id: form.tramite_id }}
        onSelect={(v) => setForm(f => ({ ...f, expediente_id: v.expediente_id, tramite_id: v.tramite_id }))}
      />

      <VinculacionModal
        open={vincularDeudaOpen}
        onClose={() => setVincularDeudaOpen(false)}
        tabs={['expedientes', 'tramites']}
        value={{ expediente_id: formDeuda.expediente_id, tramite_id: formDeuda.tramite_id }}
        onSelect={(v) => setFormDeuda(f => ({ ...f, expediente_id: v.expediente_id, tramite_id: v.tramite_id }))}
      />

      {toast && (
        <div className="toast" style={{ borderColor: toast.type === 'error' ? 'var(--red)' : 'var(--gold)', zIndex: 1200 }}>
          {toast.type === 'error' ? '⚠️' : '✓'} {toast.msg}
        </div>
      )}
    </>
  );
}
